import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * Pin buyer links (owner 2026-10-10, radaccess feat/vno-pin-buyer-link): a
 * licensed ISP or VNO adds another licensed, top-level operator as a pin buyer;
 * the buyer accepts; either side revokes. RBAC is the existing `pinorder`
 * resource: the seller invites/revokes with approve, the buyer accepts/revokes
 * with write, both list with read.
 *
 * A linked buyer orders only the seller's active wholesale plans offered to
 * all, and invoices its own subscribers in its own name.
 */
export type PinBuyerLinkStatus = "invited" | "accepted" | "revoked";

export interface PinBuyerLink {
  seller: string;
  buyer: string;
  status: PinBuyerLinkStatus;
  /** The seller's user id: returned to the seller only. */
  invitedby?: string;
  invitedat: string;
  acceptedat?: string;
  revokedat?: string;
  revokereason?: string;
}

const op = (ns: string) => `/operator/${encodeURIComponent(ns)}`;

export async function listPinBuyers(identity: CallerIdentity, seller: string, signal?: AbortSignal): Promise<PinBuyerLink[]> {
  const r = await callGateway<PinBuyerLink[] | null>(identity, config().gatewayBase, `${op(seller)}/pinbuyers`, signal ? { signal } : {});
  return r.data ?? [];
}

export async function listPinSellers(identity: CallerIdentity, buyer: string, signal?: AbortSignal): Promise<PinBuyerLink[]> {
  const r = await callGateway<PinBuyerLink[] | null>(identity, config().gatewayBase, `${op(buyer)}/pinsellers`, signal ? { signal } : {});
  return r.data ?? [];
}

export async function invitePinBuyer(identity: CallerIdentity, seller: string, buyer: string): Promise<PinBuyerLink> {
  const r = await callGateway<PinBuyerLink>(identity, config().gatewayBase, `${op(seller)}/pinbuyers`, {
    method: "POST",
    body: { buyer: normaliseNamespace(buyer) },
  });
  return r.data;
}

export async function revokePinBuyer(identity: CallerIdentity, seller: string, buyer: string, reason: string): Promise<PinBuyerLink> {
  const r = await callGateway<PinBuyerLink>(identity, config().gatewayBase, `${op(seller)}/pinbuyers/${encodeURIComponent(buyer)}/revoke`, {
    method: "PUT",
    body: { reason },
  });
  return r.data;
}

export async function acceptPinSeller(identity: CallerIdentity, buyer: string, seller: string): Promise<PinBuyerLink> {
  const r = await callGateway<PinBuyerLink>(identity, config().gatewayBase, `${op(buyer)}/pinsellers/${encodeURIComponent(seller)}/accept`, {
    method: "PUT",
  });
  return r.data;
}

export async function revokePinSeller(identity: CallerIdentity, buyer: string, seller: string, reason: string): Promise<PinBuyerLink> {
  const r = await callGateway<PinBuyerLink>(identity, config().gatewayBase, `${op(buyer)}/pinsellers/${encodeURIComponent(seller)}/revoke`, {
    method: "PUT",
    body: { reason },
  });
  return r.data;
}

/** What one side may do with a link. Revoked links are history only. */
export function linkActions(l: PinBuyerLink, side: "seller" | "buyer"): { accept: boolean; revoke: boolean } {
  return {
    accept: side === "buyer" && l.status === "invited",
    revoke: l.status === "invited" || l.status === "accepted",
  };
}

export const PIN_BUYER_LINK_LABEL: Record<PinBuyerLinkStatus, string> = {
  invited: "Invited — not yet accepted",
  accepted: "Linked",
  revoked: "Ended",
};

export function normaliseNamespace(text: string): string {
  return text.trim().toLowerCase();
}

/** A namespace as radaccess stores one: dot-separated labels, no spaces. */
export function namespaceError(text: string): string | null {
  const ns = normaliseNamespace(text);
  if (ns === "") return "Enter the operator's namespace, e.g. net.example";
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(ns)) return "Not a namespace: lowercase labels separated by dots, e.g. net.example";
  return null;
}
